import { useEffect, useState, useCallback } from "react";
import { osApi, formatINR, formatSecs, slaClass, statusColor, stageColor } from "./osApi";
import { toast } from "sonner";
import OSLeadDrawer from "./OSLeadDrawer";
import { Download, Search, Filter } from "lucide-react";

const PAGE_SIZES = [10, 25, 50];

export default function OSLeads() {
  const [meta, setMeta] = useState(null);
  const [team, setTeam] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("");
  const [assigned, setAssigned] = useState("");
  const [sort, setSort] = useState("created_desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    osApi.meta().then(setMeta).catch(() => {});
    osApi.listTeam().then(t => setTeam(t.items || [])).catch(() => setTeam([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await osApi.listLeads({
        q: q || undefined,
        stage: stage || undefined,
        source: source || undefined,
        status: status || undefined,
        assigned_agent: assigned || undefined,
        sort, page, page_size: pageSize,
      });
      setItems(res.items); setTotal(res.total);
    } catch (e) { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  }, [q, stage, source, status, assigned, sort, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const headers = ["Lead ID", "Customer", "Phone", "Email", "Source", "Location", "Budget", "Stage", "Status", "Agent"];
    const rows = items.map(l => [l.lead_id, l.customer_name, l.phone, l.email, l.source, l.location, l.budget, l.stage, l.lead_status, l.assigned_agent_name]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${(c ?? "").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ofr-leads-${Date.now()}.csv`;
    link.click();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-5" data-testid="os-leads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Leads</h1>
          <p className="text-sm mt-1" style={{color:"var(--os-text-muted)"}}>{total.toLocaleString()} leads · filter and export</p>
        </div>
        <button onClick={exportCsv} className="os-btn-ghost" data-testid="os-export-csv"><Download className="w-3.5 h-3.5" /> Export CSV</button>
      </div>

      <div className="os-card p-3 flex flex-wrap gap-2 items-center" data-testid="os-leads-filters">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{color:"var(--os-text-dim)"}}/>
          <input className="os-input pl-8" placeholder="Search name, phone, email, ID…" value={q} onChange={e => { setPage(1); setQ(e.target.value); }} data-testid="os-search" />
        </div>
        <select className="os-input max-w-[140px]" value={stage} onChange={e => { setPage(1); setStage(e.target.value); }} data-testid="os-filter-stage">
          <option value="">All stages</option>
          {meta?.stages?.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="os-input max-w-[140px]" value={source} onChange={e => { setPage(1); setSource(e.target.value); }} data-testid="os-filter-source">
          <option value="">All sources</option>
          {meta?.sources?.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="os-input max-w-[120px]" value={status} onChange={e => { setPage(1); setStatus(e.target.value); }} data-testid="os-filter-status">
          <option value="">All status</option>
          {meta?.statuses?.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="os-input max-w-[160px]" value={assigned} onChange={e => { setPage(1); setAssigned(e.target.value); }} data-testid="os-filter-agent">
          <option value="">All agents</option>
          <option value="unassigned">Unassigned</option>
          {team.filter(t => t.role !== "admin").map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className="os-input max-w-[140px]" value={sort} onChange={e => setSort(e.target.value)} data-testid="os-sort">
          <option value="created_desc">Newest</option>
          <option value="created_asc">Oldest</option>
          <option value="budget_desc">Highest budget</option>
          <option value="followup_asc">Follow-up first</option>
          <option value="name_asc">Name A-Z</option>
        </select>
      </div>

      <div className="os-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="os-table">
            <thead>
              <tr>
                <th>Lead ID</th><th>Customer</th><th>Budget</th><th>Location</th><th>Source</th>
                <th>SLA</th><th>Stage</th><th>Status</th><th>Agent</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-center py-10" style={{color:"var(--os-text-muted)"}}>Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={9} className="text-center py-10" style={{color:"var(--os-text-muted)"}}>No leads.</td></tr>}
              {items.map(l => (
                <tr key={l.id} onClick={() => setSelected(l.id)} data-testid={`os-lead-row-${l.lead_id}`}>
                  <td className="os-mono text-xs" style={{color:"var(--os-text-muted)"}}>{l.lead_id}</td>
                  <td>
                    <div className="font-medium">{l.customer_name}</div>
                    <div className="text-[11px]" style={{color:"var(--os-text-dim)"}}>{l.phone}</div>
                  </td>
                  <td className="os-mono">{formatINR(l.budget)}</td>
                  <td className="text-xs">{l.location || "—"}</td>
                  <td><span className="os-badge os-badge-blue">{l.source}</span></td>
                  <td className={`os-mono text-xs ${slaClass(l.sla_status)}`}>{l.sla_status === "Responded" ? "OK" : formatSecs(l.sla_elapsed_secs)}</td>
                  <td><span className={`os-badge ${stageColor(l.stage)}`}>{l.stage}</span></td>
                  <td><span className={`os-badge ${statusColor(l.lead_status)}`}>{l.lead_status}</span></td>
                  <td className="text-xs">{l.assigned_agent_name || <span style={{color:"var(--os-text-dim)"}}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-3 border-t" style={{borderColor:"var(--os-border)"}}>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{color:"var(--os-text-muted)"}}>Per page:</span>
            {PAGE_SIZES.map(n => (
              <button key={n} onClick={() => { setPageSize(n); setPage(1); }} data-testid={`os-page-size-${n}`}
                className={`text-xs px-2 py-1 rounded-md ${pageSize === n ? "" : ""}`}
                style={pageSize === n ? {background:"var(--os-accent)", color:"white"} : {color: "var(--os-text-muted)", border: "1px solid var(--os-border)"}}>{n}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="os-btn-ghost" data-testid="os-page-prev">Prev</button>
            <span className="text-xs os-mono" style={{color:"var(--os-text-muted)"}}>Page {page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages} className="os-btn-ghost" data-testid="os-page-next">Next</button>
          </div>
        </div>
      </div>

      {selected && <OSLeadDrawer leadId={selected} onClose={() => setSelected(null)} onUpdate={load} />}
    </div>
  );
}
